// Whether a quotation can be used: it has been created (it is not a draft) and no revised
// quotation has replaced it. A quotation needs no approval: it is active as soon as it is
// created, and only an active, un-replaced one can get a proforma or be emailed to a client.
// Used by the API and the web app so the buttons and the server agree.

export interface QuotationLike {
  requestType?: string | null
  status?: string | null
  /** Set on a quotation once a revised quotation has replaced it. */
  supersededById?: unknown
  supersededByNumber?: string | null
  /** Set when the quotation was emailed to the client from the app. */
  emailSentAt?: unknown
}

/** Created, not a draft. */
export function isQuotationIssued(doc: QuotationLike | null | undefined): boolean {
  if (!doc) return false
  const status = String(doc.status ?? '')
  return status !== '' && status !== 'draft'
}

/** A revised quotation has replaced this one: it is kept for reference but is no longer used. */
export function isQuotationSuperseded(doc: QuotationLike | null | undefined): boolean {
  return Boolean(doc && doc.supersededById)
}

/** Created and not replaced: the only kind that can get a proforma or be emailed. */
export function isQuotationUsable(doc: QuotationLike | null | undefined): boolean {
  return isQuotationIssued(doc) && !isQuotationSuperseded(doc)
}

/** The quotation was emailed to the client, so changing it now means a revised quotation. */
export function hasQuotationBeenEmailed(doc: QuotationLike | null | undefined): boolean {
  return Boolean(doc && doc.emailSentAt)
}

/** A quotation's own number stored as its "proforma number" (a bug in older records) is not a proforma. */
export function hasRealProformaNumber(doc: { number?: string | null; proformaNumber?: string | null }): boolean {
  const proforma = String(doc.proformaNumber ?? '').trim()
  return proforma !== '' && proforma !== String(doc.number ?? '').trim()
}

/** A submitted proforma is waiting for the superadmin's approval. */
export function isProformaAwaitingApproval(status: string | null | undefined): boolean {
  return status === 'proforma_requested' || status === 'proforma_under_review'
}

/**
 * The superadmin has approved the proforma (or it moved on from there): it can now be confirmed,
 * emailed and invoiced. A draft, a rejected or an awaiting proforma cannot.
 */
export function isProformaApproved(doc: { status?: string | null; isConfirmed?: boolean | null } | null | undefined): boolean {
  if (!doc) return false
  if (doc.isConfirmed) return true
  return ['proforma_approved', 'confirmed', 'invoice_pending_approval', 'sent'].includes(String(doc.status ?? ''))
}
